import { useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Search,
  TrendingUp,
  XCircle,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSeoMonitoring, type SeoRow } from "@/hooks/useSeoMonitoring";
import SeoAlertSettingsCard from "@/components/admin/SeoAlertSettingsCard";
import SeoUrlDrilldownCard from "@/components/admin/SeoUrlDrilldownCard";
import StructuredDataHealthCard from "@/components/admin/StructuredDataHealthCard";

const PERIODS = [28, 7, 90] as const;

const numberFmt = new Intl.NumberFormat("en-US");
const formatPct = (v: number) => `${(v * 100).toFixed(1)}%`;
const formatDate = (v: string | null) =>
  v ? new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

function verdictBadge(verdict: string | null) {
  if (verdict === "PASS") {
    return (
      <Badge className="gap-1 bg-primary/15 text-primary hover:bg-primary/20">
        <CheckCircle2 className="h-3 w-3" /> Indexed
      </Badge>
    );
  }
  if (verdict === "FAIL") {
    return (
      <Badge variant="destructive" className="gap-1">
        <XCircle className="h-3 w-3" /> Not indexed
      </Badge>
    );
  }
  if (verdict === "NEUTRAL" || verdict === "PARTIAL") {
    return <Badge variant="secondary">Needs review</Badge>;
  }
  return <Badge variant="outline">Unknown</Badge>;
}

function MetricCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-2xl">{value}</CardTitle>
      </CardHeader>
      {hint ? <CardContent className="pt-0 text-xs text-muted-foreground">{hint}</CardContent> : null}
    </Card>
  );
}

function RowsTable({ rows, label }: { rows: SeoRow[]; label: string }) {
  if (rows.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No {label.toLowerCase()} reported for this period yet.
      </p>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{label}</TableHead>
          <TableHead className="text-right">Impressions</TableHead>
          <TableHead className="text-right">Clicks</TableHead>
          <TableHead className="text-right">CTR</TableHead>
          <TableHead className="text-right">Avg. position</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.key}>
            <TableCell className="max-w-[320px] truncate font-medium">{row.key}</TableCell>
            <TableCell className="text-right">{numberFmt.format(row.impressions)}</TableCell>
            <TableCell className="text-right">{numberFmt.format(row.clicks)}</TableCell>
            <TableCell className="text-right">{formatPct(row.ctr)}</TableCell>
            <TableCell className="text-right">{row.position.toFixed(1)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function SeoMonitoringPanel() {
  const [days, setDays] = useState<number>(28);
  const { status, report, candidates, error, reload } = useSeoMonitoring(days);

  const changePeriod = (next: number) => {
    setDays(next);
    void reload(report?.siteUrl, next);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-semibold">
            <TrendingUp className="h-5 w-5 text-primary" /> SEO monitoring
          </h2>
          <p className="text-sm text-muted-foreground">
            Indexing status and search impressions from Google Search Console
            {report ? ` · ${report.siteUrl}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {PERIODS.map((p) => (
            <Button
              key={p}
              size="sm"
              variant={days === p ? "default" : "outline"}
              onClick={() => changePeriod(p)}
              disabled={status === "loading"}
            >
              {p}d
            </Button>
          ))}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void reload(report?.siteUrl, days)}
            disabled={status === "loading"}
            aria-label="Refresh Search Console data"
          >
            {status === "loading" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {status === "error" ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Couldn't load Search Console data</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>{error}</p>
            <Button size="sm" variant="outline" onClick={() => void reload(undefined, days)}>
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {status === "selection_required" ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Choose a Search Console property</CardTitle>
            <CardDescription>
              Several verified properties cover this site. Pick the one to report on.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {candidates.map((candidate) => (
              <Button key={candidate} variant="outline" onClick={() => void reload(candidate, days)}>
                {candidate}
              </Button>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {status === "loading" && !report ? (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
          <Skeleton className="h-72 w-full" />
        </div>
      ) : null}

      {report ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              label="Impressions"
              value={numberFmt.format(report.totals?.impressions ?? 0)}
              hint={`${report.period.startDate} → ${report.period.endDate}`}
            />
            <MetricCard label="Clicks" value={numberFmt.format(report.totals?.clicks ?? 0)} />
            <MetricCard label="Average CTR" value={formatPct(report.totals?.ctr ?? 0)} />
            <MetricCard
              label="Average position"
              value={(report.totals?.position ?? 0).toFixed(1)}
              hint="Lower is better"
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Impressions & clicks</CardTitle>
                <CardDescription>
                  Search Console reports with a 2–3 day lag, so the most recent days are excluded.
                </CardDescription>
              </CardHeader>
              <CardContent className="h-72">
                {report.timeseries.length === 0 ? (
                  <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    No reported data for this period yet.
                  </p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={report.timeseries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="impressionsFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11 }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(v: string) => v.slice(5)}
                      />
                      <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <ChartTooltip
                        contentStyle={{
                          background: "hsl(var(--popover))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 8,
                          color: "hsl(var(--popover-foreground))",
                          fontSize: 12,
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="impressions"
                        name="Impressions"
                        stroke="hsl(var(--primary))"
                        fill="url(#impressionsFill)"
                        strokeWidth={2}
                      />
                      <Line
                        type="monotone"
                        dataKey="clicks"
                        name="Clicks"
                        stroke="hsl(var(--accent-foreground))"
                        strokeWidth={2}
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-2 text-lg">
                  Indexing status
                  {verdictBadge(report.indexing?.verdict ?? null)}
                </CardTitle>
                <CardDescription className="break-all">{report.targetUrl}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {report.indexing ? (
                  <dl className="space-y-2">
                    {[
                      ["Coverage", report.indexing.coverageState],
                      ["Robots.txt", report.indexing.robotsTxtState],
                      ["Page fetch", report.indexing.pageFetchState],
                      ["Google canonical", report.indexing.googleCanonical],
                      ["Last crawl", formatDate(report.indexing.lastCrawlTime)],
                    ].map(([label, value]) => (
                      <div key={label as string} className="flex justify-between gap-3">
                        <dt className="text-muted-foreground">{label}</dt>
                        <dd className="max-w-[60%] break-all text-right font-medium">{value || "—"}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="text-muted-foreground">
                    Google hasn't reported an index state for this URL yet.
                  </p>
                )}

                <div className="border-t pt-3">
                  <p className="mb-2 font-medium">Sitemaps</p>
                  {report.sitemaps.length === 0 ? (
                    <p className="text-muted-foreground">No sitemap submitted.</p>
                  ) : (
                    <ul className="space-y-2">
                      {report.sitemaps.map((s) => (
                        <li key={s.path} className="space-y-1">
                          <p className="break-all text-xs">{s.path}</p>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span>Submitted {formatDate(s.lastSubmitted)}</span>
                            {s.errors > 0 ? (
                              <Badge variant="destructive">{s.errors} errors</Badge>
                            ) : null}
                            {s.warnings > 0 ? (
                              <Badge variant="secondary">{s.warnings} warnings</Badge>
                            ) : null}
                            {s.isPending ? <Badge variant="outline">Pending</Badge> : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Search className="h-4 w-4" /> Top performers
              </CardTitle>
              <CardDescription>Highest-impression queries and pages for this period.</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="queries">
                <TabsList>
                  <TabsTrigger value="queries">Queries</TabsTrigger>
                  <TabsTrigger value="pages">Pages</TabsTrigger>
                </TabsList>
                <TabsContent value="queries">
                  <RowsTable rows={report.topQueries} label="Query" />
                </TabsContent>
                <TabsContent value="pages">
                  <RowsTable rows={report.topPages} label="Page" />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </>
      ) : null}

      <StructuredDataHealthCard sitemaps={report?.sitemaps} />
      <SeoUrlDrilldownCard />
      <SeoAlertSettingsCard />

    </div>
  );
}

export default SeoMonitoringPanel;

